// Calculators other sites can embed with an iframe. Each embed lives at
// /embed/<lang>/<slug> (src/app/(embed)), and the copy-paste code from
// embedSnippet() carries a visible credit link to the calculator's own page.
// The credit names the brand rather than a keyword: Google treats
// keyword-rich links in distributed widgets as link spam.

export const SITE_URL = "https://oddsfantasy.com";

export type CalculatorEmbed = {
  slug: string;
  // The calculator's page on the site, without the language prefix.
  path: string;
  // Starting iframe height in pixels; embed.js resizes it to fit.
  height: number;
};

export const CALCULATOR_EMBEDS: CalculatorEmbed[] = [
  { slug: "ev-calculator", path: "/calculators/ev-calculator", height: 640 },
  { slug: "arb-calculator", path: "/calculators/arb-calculator", height: 720 },
  { slug: "vig-calculator", path: "/calculators/vig-calculator", height: 700 },
  { slug: "kelly-calculator", path: "/calculators/kelly-calculator", height: 680 },
  {
    slug: "odds-to-probability-calculator",
    path: "/calculators/odds-to-probability-calculator",
    height: 520,
  },
  { slug: "hedge-calculator", path: "/calculators/hedge-calculator", height: 720 },
  { slug: "risk-calculator", path: "/calculators/risk-calculator", height: 620 },
  { slug: "buy-odds-converter", path: "/calculators/buy-odds-converter", height: 500 },
  { slug: "parlay-calculator", path: "/more-tools/accumulator", height: 980 },
  {
    slug: "kalshi-polymarket-fee-calculator",
    path: "/calculators/kalshi-polymarket-fee-calculator",
    height: 1340,
  },
];

export function calculatorEmbed(slug: string) {
  return CALCULATOR_EMBEDS.find((embed) => embed.slug === slug) ?? null;
}

export function calculatorEmbedForPath(path: string) {
  return CALCULATOR_EMBEDS.find((embed) => embed.path === path) ?? null;
}

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

export function embedUrl(lang: string, slug: string) {
  return `${SITE_URL}/embed/${lang}/${slug}`;
}

// The HTML a site owner pastes into their page: the iframe, a credit link
// outside it (links inside an iframe belong to our page, not theirs) and the
// optional script that sizes the iframe to its content.
export function embedSnippet({
  lang,
  slug,
  name,
  byText,
}: {
  lang: string;
  slug: string;
  name: string;
  // "by" in the page's language.
  byText: string;
}) {
  const embed = calculatorEmbed(slug);
  if (!embed) return "";
  const title = escapeHtml(name);
  return [
    `<iframe src="${embedUrl(lang, slug)}" title="${title}" width="100%" height="${embed.height}" style="border:0;width:100%;max-width:760px" loading="lazy"></iframe>`,
    `<p style="font-size:13px;margin:6px 0 0">${title} ${escapeHtml(byText)} <a href="${SITE_URL}/${lang}${embed.path}">OddsFantasy</a></p>`,
    `<script async src="${SITE_URL}/embed.js"></script>`,
  ].join("\n");
}

// Message the embed page posts to its parent so embed.js can resize the iframe.
export const EMBED_HEIGHT_MESSAGE = "oddsfantasy:embed-height";
